// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Table from 'shared/components/common/Table';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import Button from 'shared/components/common/Button';
import GeneratePOsForOrdersMutation from 'main-app/mutations/GeneratePOsForOrders';
import * as Actions from 'main-app/store/Actions';

type Props = {
  history: RouterHistory,
};

const ContentWrapper = styled.div`
  flex: 1;
  padding: 24px;
`;

const EmptyState = styled.div`
  padding: 48px;
  text-align: center;
  color: #666;
  font-size: 16px;
`;

const ShortfallCell = styled.div`
  padding: 8px 12px;
  border-radius: 4px;
  font-weight: 600;
  background-color: ${props => {
    const percentage = (props.shortfall / props.required) * 100;
    if (percentage > 75) return '#ffebee'; // Critical - red
    if (percentage > 25) return '#fff9c4'; // Moderate - yellow
    return 'transparent'; // Minor
  }};
  color: ${props => {
    const percentage = (props.shortfall / props.required) * 100;
    if (percentage > 75) return '#c62828'; // Critical - dark red
    if (percentage > 25) return '#f57f17'; // Moderate - dark yellow
    return '#1a1a1a'; // Minor
  }};
`;

const SourceOrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OrderLink = styled.a`
  color: #4a90e2;
  text-decoration: none;
  cursor: pointer;
  font-size: 13px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const OrderBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: #e0e0e0;
  border-radius: 12px;
  font-size: 12px;
  color: #666;
  margin-left: 8px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

class MaterialRequirements extends PureComponent<Props> {
  handleGeneratePOs = (materialRequirements: Array<any>) => {
    if (!materialRequirements || materialRequirements.length === 0) {
      Actions.alertNotification(
        i18n.t('No material requirements to generate POs for'),
        'warning'
      );
      return;
    }

    const confirmMessage = i18n.t(
      `This will generate purchase orders for ${materialRequirements.length} material requirement(s). Continue?`
    );

    if (!window.confirm(confirmMessage)) {
      return;
    }

    Actions.alertNotification(
      i18n.t('Generating purchase orders for material requirements...'),
      'info'
    );

    GeneratePOsForOrdersMutation.commit(
      {},
      {
        onSuccess: response => {
          const count = response?.generatePOsForOrders?.purchaseOrders?.length || 0;
          const itemsProcessed = response?.generatePOsForOrders?.itemsProcessed || 0;
          
          console.log('PO Generation Response (Material Requirements):', {
            purchaseOrdersCount: count,
            itemsProcessed: itemsProcessed,
            fullResponse: response
          });

          if (count > 0) {
            Actions.alertNotification(
              i18n.t(`Successfully generated ${count} purchase order(s) for material requirements!`),
              'success'
            );
            
            // Navigate to PO list to see generated orders
            setTimeout(() => {
              this.props.history.push('/purchase-orders');
            }, 1500);
          } else {
            Actions.alertNotification(
              i18n.t(`No purchase orders were generated. Items processed: ${itemsProcessed}. Check backend logs and vendor pricing.`),
              'warning'
            );
          }
        },
        onFailure: error => {
          Actions.alertNotification(
            i18n.t(`Failed to generate purchase orders: ${error.message}`),
            'error'
          );
        },
      }
    );
  };

  renderSourceOrders = (sourceOrders: Array<any>) => {
    if (!sourceOrders || sourceOrders.length === 0) {
      return <span style={{ color: '#999' }}>No orders</span>;
    }

    // Show first 3 orders, then count badge if more
    const displayOrders = sourceOrders.slice(0, 3);
    const remainingCount = sourceOrders.length - 3;

    return (
      <SourceOrdersList>
        {displayOrders.map(order => (
          <OrderLink
            key={order.id}
            onClick={() => this.props.history.push(`/order/${order.id}`)}
          >
            {order.orderNumber}
            {order.requiredAt && ` (Required: ${order.requiredAt})`}
          </OrderLink>
        ))}
        {remainingCount > 0 && (
          <OrderBadge>+{remainingCount} more</OrderBadge>
        )}
      </SourceOrdersList>
    );
  };

  render() {
    const { history } = this.props;

    return (
      <Page>
        <QueryRenderer
          environment={relayEnvironment}
          query={graphql`
            query MaterialRequirementsQuery {
              materialRequirements {
                item {
                  id
                  itemNumber
                  name
                }
                onHand
                required
                shortfall
                sourceOrders {
                  id
                  orderNumber
                  requiredAt
                  status
                }
              }
            }
          `}
          render={({ props, error }) => {
            if (error) {
              return (
                <EmptyState>
                  {i18n.t('Error loading material requirements')}
                </EmptyState>
              );
            }

            if (!props) {
              return <Loader />;
            }

            const { materialRequirements } = props;
            const hasRequirements = materialRequirements && materialRequirements.length > 0;

            return (
              <>
                <PageHeaderList
                  title={i18n.t('Material Requirements')}
                  subtitle={i18n.t('Material shortfalls from open production orders')}
                >
                  <HeaderActions>
                    {hasRequirements && (
                      <Button
                        buttonType="primary"
                        onClick={() => this.handleGeneratePOs(materialRequirements)}
                      >
                        {i18n.t('Generate POs for All')}
                      </Button>
                    )}
                  </HeaderActions>
                </PageHeaderList>

                <ContentWrapper>
                  {!hasRequirements ? (
                    <EmptyState>
                      {i18n.t('No material requirements found. All materials are in stock!')}
                    </EmptyState>
                  ) : (
                    <Table
                      columns={[
                        {
                          Header: i18n.t('Item Number'),
                          accessor: 'itemNumber',
                          width: 150,
                        },
                        {
                          Header: i18n.t('Item Name'),
                          accessor: 'itemName',
                          width: 250,
                        },
                        {
                          Header: i18n.t('Current Qty'),
                          accessor: 'onHand',
                          width: 120,
                          style: { textAlign: 'right' },
                        },
                        {
                          Header: i18n.t('Required'),
                          accessor: 'required',
                          width: 120,
                          style: { textAlign: 'right' },
                        },
                        {
                          Header: i18n.t('Shortfall'),
                          accessor: 'shortfall',
                          width: 120,
                          style: { textAlign: 'right' },
                        },
                        {
                          Header: i18n.t('Source Orders'),
                          accessor: 'sourceOrders',
                          width: 200,
                        },
                      ]}
                      data={materialRequirements.map(req => ({
                        id: req.item.id,
                        itemNumber: req.item.itemNumber,
                        itemName: req.item.name,
                        onHand: req.onHand?.toFixed(2) || '0.00',
                        required: req.required?.toFixed(2) || '0.00',
                        shortfall: (
                          <ShortfallCell
                            shortfall={req.shortfall}
                            required={req.required}
                          >
                            {req.shortfall?.toFixed(2) || '0.00'}
                          </ShortfallCell>
                        ),
                        sourceOrders: this.renderSourceOrders(req.sourceOrders),
                      }))}
                    />
                  )}
                </ContentWrapper>
              </>
            );
          }}
        />
      </Page>
    );
  }
}

export default MaterialRequirements;
